<template>
  <v-card class="campaign-card" elevation="1">
    <!-- HEADER -->
    <v-card-title class="pa-0">
      <v-toolbar flat dense color="transparent" class="px-4">
        <v-toolbar-title class="d-flex align-center">
          <v-avatar size="36" color="primary" class="white--text mr-3">
            <v-icon>mdi-account-group</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="text-h6 font-weight-bold">Campaign Send</span>
            <span class="text-caption grey--text">{{ userList.length }} prospects</span>
          </div>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search prospects..."
          hide-details
          dense
          filled
          rounded
          class="search-field"
        ></v-text-field>
      </v-toolbar>
    </v-card-title>

    <!-- MESSAGE COMPOSER (Collapsible) -->
    <v-card-text class="pa-0">
      <v-expansion-panels v-model="composerPanel" flat accordion>
        <v-expansion-panel>
          <v-expansion-panel-header class="composer-header">
            <div class="d-flex align-center">
              <v-icon left color="primary" small>mdi-pencil</v-icon>
              <span class="font-weight-medium">Message Composer</span>
              <v-chip x-small class="ml-3" :color="hasMessage ? 'success' : 'grey'" dark>
                {{ hasMessage ? 'Ready' : 'Empty' }}
              </v-chip>
              <v-spacer></v-spacer>
              <span v-if="advertising_message.title" class="text-caption grey--text truncate-preview">
                {{ advertising_message.title }}
              </span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-container class="py-4 grey lighten-4 rounded-lg">
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="advertising_message.title"
                    label="Subject"
                    dense
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-format-title"
                    @input="editing = true"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="advertising_message.link"
                    label="CTA Link"
                    dense
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-link"
                    @input="editing = true"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="advertising_message.get_file"
                    label="Attachment"
                    dense
                    outlined
                    hide-details
                    prepend-inner-icon="mdi-paperclip"
                    readonly
                  >
                    <template v-slot:append>
                      <v-btn icon x-small @click="copyFileUrl" title="Copy URL">
                        <v-icon x-small>mdi-content-copy</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="advertising_message.content"
                    label="Message body"
                    dense
                    outlined
                    rows="3"
                    auto-grow
                    hide-details
                    prepend-inner-icon="mdi-text"
                    @input="editing = true"
                  ></v-textarea>
                </v-col>
              </v-row>

              <!-- LIVE PREVIEW -->
              <v-divider class="my-3"></v-divider>
              <div class="d-flex align-center mb-2">
                <v-icon x-small class="mr-2">mdi-eye</v-icon>
                <span class="text-caption font-weight-medium grey--text text--darken-1">PREVIEW</span>
              </div>
              <v-alert
                outlined
                color="primary"
                class="mb-0 message-preview"
                icon="mdi-cellphone-message"
              >
                <div class="font-weight-bold">{{ advertising_message.title || 'No subject' }}</div>
                <div class="text-body-2 mt-1" style="white-space: pre-wrap;">
                  {{ advertising_message.content || 'No content yet...' }}
                </div>
                <div v-if="advertising_message.link" class="mt-2">
                  <v-chip x-small color="primary" outlined>
                    <v-icon left x-small>mdi-open-in-new</v-icon>
                    {{ advertising_message.link }}
                  </v-chip>
                </div>
              </v-alert>
            </v-container>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>

    <!-- BULK ACTION BAR (Sticky) -->
    <v-slide-y-transition>
      <v-sheet v-if="selectedProspects.length" color="primary lighten-5" class="bulk-bar px-4 py-2 d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-check-circle</v-icon>
        <span class="font-weight-bold primary--text">{{ selectedProspects.length }} selected</span>
        <v-spacer></v-spacer>
        <v-btn
          v-for="channel in availableBulkChannels"
          :key="channel.key"
          :color="channel.color"
          :disabled="!channel.enabled || bulkSending"
          small
          depressed
          class="mr-2 white--text"
          @click="channel.action"
        >
          <v-icon left small>{{ channel.icon }}</v-icon>
          {{ channel.label }}
        </v-btn>
        <v-btn icon small @click="selectedProspects = []">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-sheet>
    </v-slide-y-transition>

    <!-- DATA TABLE -->
    <v-data-table
      v-model="selectedProspects"
      :headers="headers"
      :items="userList"
      item-key="id"
      show-select
      :items-per-page="10"
      :search="search"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
      class="elevation-0 prospects-table"
    >
      <!-- Prospect cell -->
      <template #[`item.prospect`]="{ item }">
        <div class="d-flex align-center py-2">
          <v-avatar size="40" :color="item.user_id ? 'primary' : 'grey lighten-2'" class="mr-3">
            <span class="white--text text-subtitle-2">{{ getInitials(item.name) }}</span>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-semibold">{{ item.name }}</span>
            <div class="d-flex align-center">
              <v-chip x-small :color="item.profession ? 'blue lighten-5' : 'grey lighten-3'" label class="mr-1">
                {{ item.profession || 'Unknown' }}
              </v-chip>
              <span class="text-caption grey--text">{{ item.phone_number }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Available channels indicators -->
      <template #[`item.channels`]="{ item }">
        <div class="d-flex gap-1">
          <v-tooltip v-for="ch in getChannels(item)" :key="ch.key" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-avatar
                size="28"
                :color="ch.available ? ch.color + ' lighten-5' : 'grey lighten-3'"
                class="channel-avatar"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon
                  :color="ch.available ? ch.color : 'grey lighten-1'"
                  x-small
                >{{ ch.icon }}</v-icon>
              </v-avatar>
            </template>
            <span>{{ ch.available ? ch.label : ch.label + ' (unavailable)' }}</span>
          </v-tooltip>
        </div>
      </template>

      <!-- Actions -->
      <template #[`item.actions`]="{ item }">
        <div class="d-flex align-center action-group">
          <v-btn
            v-for="ch in getChannels(item).filter(c => c.available)"
            :key="ch.key"
            icon
            x-small
            :color="ch.color"
            class="action-btn"
            :loading="actionLoading[`${item.id}-${ch.key}`]"
            @click="ch.action(item)"
          >
            <v-icon x-small>{{ ch.icon }}</v-icon>
          </v-btn>
          <v-btn
            v-if="!getChannels(item).some(c => c.available)"
            icon
            x-small
            disabled
            title="No contact methods available"
          >
            <v-icon x-small>mdi-alert-circle</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- Status -->
      <template #[`item.status`]="{ item }">
        <v-chip
          x-small
          :color="getStatusColor(item)"
          :text-color="getStatusTextColor(item)"
          label
        >
          {{ getStatusLabel(item) }}
        </v-chip>
      </template>
    </v-data-table>

    <!-- Loading -->
    <v-overlay :value="loading" absolute opacity="0.8">
      <v-progress-circular indeterminate color="primary" size="48" />
      <div class="mt-2 text-caption white--text">Loading prospects...</div>
    </v-overlay>
  </v-card>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CampaignSend',

  data() {
    return {
      headers: [
        { text: 'Prospect', value: 'prospect', sortable: false },
        { text: 'Channels', value: 'channels', sortable: false, align: 'center', width: '180px' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '160px' },
        { text: 'Status', value: 'status', sortable: false, align: 'center', width: '100px' },
      ],
      userList: [],
      advertising_message: {
        title: '',
        content: '',
        link: '',
        get_file: '',
      },
      selectedProspects: [],
      search: '',
      loading: false,
      bulkSending: false,
      composerPanel: 0,
      editing: false,
      sentStatus: {},
      // Track individual action loading states: key = `${prospectId}-${channel}`
      actionLoading: {},
    }
  },

  computed: {
    hasMessage() {
      return !!(this.advertising_message.title || this.advertising_message.content)
    },

    availableBulkChannels() {
      return [
        {
          key: 'whatsapp',
          label: 'WhatsApp',
          icon: 'mdi-whatsapp',
          color: 'green',
          enabled: this.selectedProspects.some(p => p.phone_number),
          action: this.bulkSendWhatsApp,
        },
        {
          key: 'chat',
          label: 'Chat',
          icon: 'mdi-message-text',
          color: 'blue darken-2',
          enabled: this.selectedProspects.some(p => p.user_id),
          action: this.bulkSendChat,
        },
        {
          key: 'email',
          label: 'Email',
          icon: 'mdi-email',
          color: 'orange',
          // Email requires user_id to use the partner endpoint
          enabled: this.selectedProspects.some(p => p.user_id || p.email),
          action: this.bulkSendEmail,
        },
        {
          key: 'webpush',
          label: 'WebPush',
          icon: 'mdi-bell',
          color: 'deep-orange',
          // WebPush requires user_id (registered user)
          enabled: this.selectedProspects.some(p => p.user_id),
          action: this.bulkSendWebpush,
        },
        {
          key: 'sms',
          label: 'SMS',
          icon: 'mdi-message',
          color: 'grey darken-2',
          enabled: this.selectedProspects.some(p => p.phone_number),
          action: this.bulkSendSMS,
        },
      ]
    },
  },

  mounted() {
    this.getProspectsAdMessage()
    document.title = 'TopUp | Campaign Message'
  },

  methods: {
    // ========== DATA FETCHING ==========
    async getProspectsAdMessage() {
      this.loading = true
      const { group_id, message_id, message_means, start_date, end_date } = this.$route.params

      const formData = new FormData()
      formData.append('group_id', group_id)
      formData.append('message_id', message_id)
      formData.append('message_means', message_means)
      formData.append('start_date', start_date)
      formData.append('end_date', end_date)

      try {
        const response = await axios.post('/api/v1/topup/get/prospects/ad_message/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.userList = response.data.prospects.map(p => ({
          ...p,
          user_id: p.user_id || null,
          email: p.email || '',
          phone_number: p.phone_number || '',
        }))
        this.advertising_message = response.data.advertising_message
      } catch (error) {
        this.showToast('Failed to load prospects', 'error')
      } finally {
        this.loading = false
      }
    },

    // ========== CHANNEL CONFIGURATION ==========
    getChannels(item) {
      return [
        {
          key: 'whatsapp',
          label: 'WhatsApp',
          icon: 'mdi-whatsapp',
          color: 'green',
          available: !!item.phone_number,
          action: this.sendWhatsApp,
        },
        {
          key: 'chat',
          label: 'Chat',
          icon: 'mdi-message-text',
          color: 'blue darken-2',
          available: !!item.user_id,
          action: this.sendChat,
        },
        {
          key: 'email',
          label: 'Email',
          icon: 'mdi-email',
          color: 'orange',
          // Prefer partner endpoint (user_id), fallback to bulk-email if only email
          available: !!(item.user_id || item.email),
          action: this.sendEmail,
        },
        {
          key: 'webpush',
          label: 'WebPush',
          icon: 'mdi-bell',
          color: 'deep-orange',
          // WebPush only works for registered users (user_id required by Django endpoint)
          available: !!item.user_id,
          action: this.sendWebpush,
        },
        {
          key: 'sms',
          label: 'SMS',
          icon: 'mdi-message',
          color: 'grey darken-2',
          available: !!item.phone_number,
          action: this.sendSMS,
        },
      ]
    },

    // ========== MESSAGE BUILDER ==========
    buildMessage() {
      const parts = []
      if (this.advertising_message.title) parts.push(this.advertising_message.title)
      if (this.advertising_message.content) parts.push(this.advertising_message.content)
      if (this.advertising_message.link) parts.push(this.advertising_message.link)
      return parts.join('\n\n')
    },

    // ========== INDIVIDUAL ACTIONS ==========

    sendWhatsApp(prospect) {
      const phone = this.formatPhone(prospect.phone_number)
      if (!phone) return this.showToast('No valid phone number', 'warning')
      const message = this.buildMessage()
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank')
      this.markSent(prospect.id, 'whatsapp')
      this.showToast(`WhatsApp opened for ${prospect.name}`, 'success')
    },

    sendChat(prospect) {
      if (!prospect.user_id) return
      this.sendMessageToUser(prospect.user_id, prospect.name)
      this.markSent(prospect.id, 'chat')
    },

    /**
     * Email — uses the same Django endpoint as PartnersList:
     *   POST /api/v1/manager/partners/<user_id>/send-email/
     *   payload: { subject, message }
     *
     * Falls back to /api/v1/topup/send/bulk-email/ if the prospect
     * has no user_id but does have a raw email address.
     */
    async sendEmail(prospect) {
      if (!prospect.user_id && !prospect.email) {
        return this.showToast('No email contact available', 'warning')
      }

      this.$set(this.actionLoading, `${prospect.id}-email`, true)

      try {
        if (prospect.user_id) {
          // ── Partner endpoint (same as PartnersList) ──────────────
          await axios.post(
            `/api/v1/manager/partners/${prospect.user_id}/send-email/`,
            {
              subject: this.advertising_message.title || 'Message from PettyCash',
              message: this.buildMessage(),
            }
          )
        } else {
          // ── Fallback: prospect has an email but isn't registered ──
          await axios.post('/api/v1/topup/send/bulk-email/', {
            emails: [prospect.email],
            subject: this.advertising_message.title,
            body: this.advertising_message.content,
            link: this.advertising_message.link,
            file_url: this.advertising_message.get_file || '',
          })
        }
        this.markSent(prospect.id, 'email')
        this.showToast(`Email sent to ${prospect.name}`, 'success')
      } catch (err) {
        const msg = err.response?.data?.error || `Failed to send email to ${prospect.name}`
        this.showToast(msg, 'error')
      } finally {
        this.$set(this.actionLoading, `${prospect.id}-email`, false)
      }
    },

    /**
     * WebPush — uses the same Django endpoint as PartnersList:
     *   POST /api/v1/manager/partners/<user_id>/send-webpush/
     *   payload: { title, message }
     *
     * Requires user_id (registered user with a push subscription).
     */
    async sendWebpush(prospect) {
      if (!prospect.user_id) {
        return this.showToast('WebPush requires a registered user', 'warning')
      }

      this.$set(this.actionLoading, `${prospect.id}-webpush`, true)

      try {
        await axios.post(
          `/api/v1/manager/partners/${prospect.user_id}/send-webpush/`,
          {
            title: this.advertising_message.title || 'Message from Manager',
            message: this.buildMessage(),
          }
        )
        this.markSent(prospect.id, 'webpush')
        this.showToast(`WebPush sent to ${prospect.name}`, 'success')
      } catch (err) {
        const msg = err.response?.data?.error || `Failed to send WebPush to ${prospect.name}`
        this.showToast(msg, 'error')
      } finally {
        this.$set(this.actionLoading, `${prospect.id}-webpush`, false)
      }
    },

    sendSMS(prospect) {
      const phone = this.formatPhone(prospect.phone_number)
      if (!phone) return this.showToast('No valid phone number', 'warning')
      const body = this.buildMessage()
      window.open(`sms:${phone}?body=${encodeURIComponent(body)}`, '_blank')
      this.markSent(prospect.id, 'sms')
      this.showToast(`SMS opened for ${prospect.name}`, 'success')
    },

    // ========== BULK ACTIONS ==========

    bulkSendWhatsApp() {
      const targets = this.selectedProspects.filter(p => p.phone_number)
      targets.forEach(p => this.sendWhatsApp(p))
      this.selectedProspects = []
    },

    bulkSendChat() {
      const targets = this.selectedProspects.filter(p => p.user_id)
      if (!targets.length) return this.showToast('No registered users selected', 'warning')
      targets.forEach(p => this.sendChat(p))
      this.selectedProspects = []
    },

    /**
     * Bulk email — sends sequentially via the partner endpoint for registered
     * users, and via the legacy bulk-email endpoint for unregistered prospects.
     */
    async bulkSendEmail() {
      const registered   = this.selectedProspects.filter(p => p.user_id)
      const unregistered = this.selectedProspects.filter(p => !p.user_id && p.email)

      if (!registered.length && !unregistered.length) {
        return this.showToast('No prospects with email selected', 'warning')
      }

      this.bulkSending = true
      let successCount = 0

      // Registered users → partner endpoint (one call per user)
      for (const prospect of registered) {
        try {
          await axios.post(
            `/api/v1/manager/partners/${prospect.user_id}/send-email/`,
            {
              subject: this.advertising_message.title || 'Message from PettyCash',
              message: this.buildMessage(),
            }
          )
          this.markSent(prospect.id, 'email')
          successCount++
        } catch (err) {
          console.error(`Email failed for ${prospect.name}`, err)
        }
      }

      // Unregistered prospects → legacy bulk endpoint (single call)
      if (unregistered.length) {
        try {
          const res = await axios.post('/api/v1/topup/send/bulk-email/', {
            emails: unregistered.map(p => p.email),
            subject: this.advertising_message.title,
            body: this.advertising_message.content,
            link: this.advertising_message.link,
            file_url: this.advertising_message.get_file || '',
          })
          unregistered.forEach(p => this.markSent(p.id, 'email'))
          successCount += res.data.success || unregistered.length
        } catch (err) {
          this.showToast('Failed to send some emails (unregistered prospects)', 'error')
        }
      }

      this.bulkSending = false
      this.showToast(`Email sent to ${successCount} recipient(s)`, 'success')
      this.selectedProspects = []
    },

    /**
     * Bulk WebPush — uses the same partner endpoint sequentially.
     * Only registered users (user_id required).
     */
    async bulkSendWebpush() {
      const targets = this.selectedProspects.filter(p => p.user_id)
      if (!targets.length) {
        return this.showToast('No registered users selected for WebPush', 'warning')
      }

      this.bulkSending = true
      let successCount = 0

      for (const prospect of targets) {
        try {
          await axios.post(
            `/api/v1/manager/partners/${prospect.user_id}/send-webpush/`,
            {
              title: this.advertising_message.title || 'Message from Manager',
              message: this.buildMessage(),
            }
          )
          this.markSent(prospect.id, 'webpush')
          successCount++
        } catch (err) {
          console.error(`WebPush failed for ${prospect.name}`, err)
        }
      }

      this.bulkSending = false
      this.showToast(`WebPush sent to ${successCount} recipient(s)`, 'success')
      this.selectedProspects = []
    },

    bulkSendSMS() {
      const targets = this.selectedProspects.filter(p => p.phone_number)
      targets.forEach(p => this.sendSMS(p))
      this.selectedProspects = []
    },

    // ========== API CHAT ==========
    async sendMessageToUser(downlineId, name) {
      try {
        const fd = new FormData()
        fd.append('downline_id', downlineId)
        fd.append('message', this.buildMessage())
        fd.append('message_type', 'text')
        await axios.post('/api/v1/topup/send_to_downline/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.showToast(`Message sent to ${name}`, 'success')
      } catch (err) {
        this.showToast(`Failed to send to ${name}`, 'error')
      }
    },

    // ========== HELPERS ==========
    formatPhone(raw) {
      if (!raw) return ''
      let digits = raw.replace(/\D/g, '')
      if (digits.startsWith('0')) digits = digits.substring(1)
      if (digits.length === 9) digits = '237' + digits
      return digits ? `+${digits}` : ''
    },

    getInitials(name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },

    copyFileUrl() {
      navigator.clipboard.writeText(this.advertising_message.get_file || '')
      this.showToast('File URL copied', 'success')
    },

    markSent(prospectId, channel) {
      if (!this.sentStatus[prospectId]) this.$set(this.sentStatus, prospectId, [])
      if (!this.sentStatus[prospectId].includes(channel)) {
        this.sentStatus[prospectId].push(channel)
      }
    },

    getStatusColor(item) {
      const sent = this.sentStatus[item.id] || []
      if (sent.length === 0) return 'grey lighten-3'
      if (sent.length >= 2) return 'success lighten-4'
      return 'info lighten-4'
    },

    getStatusTextColor(item) {
      const sent = this.sentStatus[item.id] || []
      if (sent.length === 0) return 'grey darken-1'
      if (sent.length >= 2) return 'success darken-2'
      return 'info darken-2'
    },

    getStatusLabel(item) {
      const sent = this.sentStatus[item.id] || []
      if (sent.length === 0) return 'Pending'
      if (sent.length >= 2) return 'Multi-sent'
      return 'Sent'
    },

    showToast(msg, type) {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', type === 'success' ? 'teal darken-4' : 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
.campaign-card {
  border-radius: 12px;
  overflow: hidden;
}

.search-field {
  max-width: 280px;
  transition: all 0.3s ease;
}

.composer-header {
  background: #f5f5f5;
  min-height: 48px !important;
}

.truncate-preview {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-preview {
  border-radius: 8px;
  background: white !important;
}

.bulk-bar {
  position: sticky;
  top: 0;
  z-index: 5;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.prospects-table ::v-deep th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0,0,0,0.6) !important;
}

.gap-1 {
  gap: 4px;
}

.channel-avatar {
  transition: transform 0.2s;
}

.channel-avatar:hover {
  transform: scale(1.1);
}

.action-group {
  gap: 2px;
}

.action-btn {
  transition: all 0.2s;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
</style>
